import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ORGANIZATION_PERMISSIONS } from '../../pages/Organization/consts';
import {
  UpdateUserResponse,
  GetUserResponse,
  GetOrganizationMembersResponse,
  GetTeamsResponse,
} from '../httpClients/PlatformClient/api.schema';
import { PlatformClient } from '../httpClients/PlatformClient/PlatformClient';
import { QUERY_KEYS } from '../queries/consts';
import { MUTATION_KEYS } from './consts';

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_USER],
    mutationFn: PlatformClient.updateUser,
    onSuccess: (data: UpdateUserResponse) => {
      const currentUser = queryClient.getQueryData<GetUserResponse>([
        QUERY_KEYS.USER,
      ]);

      const isUpdatingCurrentUser = currentUser?.user.id === data?.user.id;

      if (isUpdatingCurrentUser) {
        queryClient.setQueryData([QUERY_KEYS.USER], () => {
          return data;
        });
      }

      // Update the specific user in all organization members queries
      queryClient.setQueriesData(
        { queryKey: [QUERY_KEYS.ORGANIZATION_MEMBERS] },
        (oldData: GetOrganizationMembersResponse) => {
          if (!oldData) return oldData;

          // Get organization teams to resolve team names
          const organizationTeams = queryClient.getQueryData<GetTeamsResponse>([
            QUERY_KEYS.ORGANIZATION_TEAMS,
          ]);

          return {
            ...oldData,
            data: oldData.data.map((member) => {
              if (member.email === data?.user.email) {
                const user = data.user;
                const userTeam = user.teams[0];

                // Find team name from organization teams
                let teamName = userTeam?.team_id;

                if (userTeam && organizationTeams?.teams) {
                  const team = organizationTeams.teams.find(
                    (t) => t.id === userTeam.team_id,
                  );

                  teamName = team?.name || userTeam.team_id;
                }

                return {
                  ...member,
                  enterprise_user_name: user.enterprise_user_name,
                  is_organization_owner:
                    user.organization_permission ===
                    ORGANIZATION_PERMISSIONS.OWNER,
                  baseUrl: user.baseUrl,
                  team_role: userTeam?.permission || null,
                  team: userTeam
                    ? {
                        id: userTeam.team_id,
                        name: teamName,
                      }
                    : null,
                };
              }

              return member;
            }),
          };
        },
      );

      // Invalidate all team-users queries to refetch with updated data
      queryClient.invalidateQueries({
        queryKey: ['team-users'],
      });

      // Invalidate organization teams to ensure team data is fresh
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_TEAMS],
      });

      // Invalidate organization env members when user baseUrl is updated
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_ENV_MEMBERS],
      });
    },
  });
};
